import React, { useCallback, useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import Divider from "@mui/material/Divider";
import SendIcon from "@mui/icons-material/Send";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import RestoreIcon from "@mui/icons-material/Restore";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import MicIcon from "@mui/icons-material/Mic";
import MicOffIcon from "@mui/icons-material/MicOff";
import GenericAlertModal from "../../../components/modals/GenericAlertModal";
import {
	useStartSmartProgramChatTurn,
	useSmartProgramChatSession,
} from "../../../hooks/useTreatmentPlans";
import {
	ChatSessionStatus,
	SmartProgramChatSession,
} from "../../../services/treatmentPlan.service";
import {
	ChatMessageBubble,
	chatStatusColor,
	chatStatusLabel,
} from "./chat/ChatMessageBubble";
import { useSpeechToText } from "./chat/useSpeechToText";
import { useScrollToLatest } from "./chat/useScrollToLatest";

interface SmartProgramChatProps {
	treatmentPlanId: string;
	onGenerate: (renderedDescription: string) => void;
	onCancel: () => void;
	isFinalGenerating: boolean;
	/**
	 * Validation error from the most recent one-shot generation attempt, if it
	 * failed schema validation. Chat will auto-inject this into the
	 * conversation (once per jobId) so the assistant can help refine.
	 */
	finalGenerationError?: string | null;
	finalGenerationJobId?: string | null;
}

// Shared with the treatment-config assistant — see ./chat.
const statusLabel = chatStatusLabel;
const statusColor = chatStatusColor;

const renderRenderedDescription = (
	session: SmartProgramChatSession,
): string => {
	const lines: string[] = [];
	lines.push(
		"The following Smart Program design was captured through an interactive conversation with the clinician.",
	);
	lines.push("");
	if (session.draft) {
		lines.push("Final design summary (JSON):");
		lines.push(JSON.stringify(session.draft, null, 2));
		lines.push("");
	}
	lines.push("Conversation transcript:");
	for (const m of session.messages) {
		lines.push(
			`${m.role === "USER" ? "Clinician" : "Assistant"}: ${m.content}`,
		);
	}
	lines.push("");
	lines.push(
		"Now generate the complete workflow JSON following all the rules in your system prompt (manual-approval pattern, casing, action types, etc.).",
	);
	return lines.join("\n");
};

const DraftSidebar: React.FC<{
	draft: Record<string, unknown> | null;
	status: ChatSessionStatus | null;
	onGenerate: () => void;
	canGenerate: boolean;
	isFinalGenerating: boolean;
}> = ({ draft, status, onGenerate, canGenerate, isFinalGenerating }) => {
	return (
		<Paper variant="outlined" sx={{ p: 2, height: "100%" }}>
			<Stack
				direction="row"
				justifyContent="space-between"
				alignItems="center"
				sx={{ mb: 1 }}
			>
				<Typography variant="subtitle1">Draft</Typography>
				{status && (
					<Chip
						size="small"
						label={statusLabel[status]}
						color={statusColor[status]}
					/>
				)}
			</Stack>
			<Divider sx={{ mb: 2 }} />

			{!draft && (
				<Typography variant="body2" color="text.secondary">
					The assistant will build a running summary of your program here as you
					chat.
				</Typography>
			)}

			{draft && (
				<Box
					component="pre"
					sx={{
						fontFamily: "monospace",
						fontSize: 12,
						bgcolor: "grey.50",
						p: 1.5,
						borderRadius: 1,
						maxHeight: 360,
						overflow: "auto",
						whiteSpace: "pre-wrap",
						wordBreak: "break-word",
					}}
				>
					{JSON.stringify(draft, null, 2)}
				</Box>
			)}

			<Button
				fullWidth
				variant="contained"
				color="primary"
				startIcon={
					isFinalGenerating ? (
						<CircularProgress size={16} color="inherit" />
					) : (
						<AutoAwesomeIcon />
					)
				}
				onClick={onGenerate}
				disabled={!canGenerate || isFinalGenerating}
				sx={{ mt: 2 }}
			>
				{isFinalGenerating
					? "Generating…"
					: status === "READY_TO_GENERATE"
						? "Generate workflow"
						: "Generate anyway"}
			</Button>
			{status !== "READY_TO_GENERATE" && (
				<Typography
					variant="caption"
					color="text.secondary"
					sx={{ display: "block", mt: 1 }}
				>
					You can generate at any time, but the assistant will tell you when it
					thinks the design is complete.
				</Typography>
			)}
		</Paper>
	);
};

const sessionStorageKey = (treatmentPlanId: string): string =>
	`sims-smart-program-chat-session:${treatmentPlanId}`;

const SmartProgramChat: React.FC<SmartProgramChatProps> = ({
	treatmentPlanId,
	onGenerate,
	onCancel,
	isFinalGenerating,
	finalGenerationError,
	finalGenerationJobId,
}) => {
	// Restore sessionId from localStorage on mount so close-and-reopen survives.
	const [sessionId, setSessionId] = useState<string | null>(() => {
		try {
			return localStorage.getItem(sessionStorageKey(treatmentPlanId));
		} catch {
			return null;
		}
	});
	const [composer, setComposer] = useState("");

	// True only on the initial render if we restored a sessionId from storage.
	// Used to show a one-time "Resumed earlier session" hint.
	const [didResumeFromStorage] = useState<boolean>(() => {
		try {
			return !!localStorage.getItem(sessionStorageKey(treatmentPlanId));
		} catch {
			return false;
		}
	});
	const [resumeHintDismissed, setResumeHintDismissed] = useState(false);
	const [showStartOverConfirm, setShowStartOverConfirm] = useState(false);
	// Tracks the generation jobId we've already auto-injected an error turn for,
	// so a poll cycle doesn't double-post the same validation error.
	const [autoInjectedForJobId, setAutoInjectedForJobId] = useState<
		string | null
	>(null);

	const appendFinalSpeech = useCallback((finalText: string) => {
		const trimmed = finalText.trim();
		if (!trimmed) return;
		setComposer((prev) => {
			if (!prev) return trimmed;
			return prev.endsWith(" ") ? prev + trimmed : prev + " " + trimmed;
		});
	}, []);

	const [pendingAutoSend, setPendingAutoSend] = useState(false);

	const speech = useSpeechToText({
		onFinal: appendFinalSpeech,
		silenceMs: 15_000,
		stopWordPattern: /\bsend\s+message\b/i,
		onStopWord: () => setPendingAutoSend(true),
	});

	const startTurnMutation = useStartSmartProgramChatTurn();
	const sessionQuery = useSmartProgramChatSession(
		treatmentPlanId,
		sessionId,
		!!sessionId,
	);

	const session = sessionQuery.data ?? null;

	// If the restored sessionId is invalid (404), wipe it and start fresh.
	useEffect(() => {
		if (sessionQuery.isError && sessionId) {
			try {
				localStorage.removeItem(sessionStorageKey(treatmentPlanId));
			} catch {
				// ignore
			}
			setSessionId(null);
		}
	}, [sessionQuery.isError, sessionId, treatmentPlanId]);

	// Scroll to bottom on new messages or while thinking
	const transcriptRef = useScrollToLatest([
		session?.messages.length,
		session?.status,
	]);

	// Auto-inject validation error from a failed one-shot generation into the
	// chat as a user turn, so the assistant can help the clinician refine the
	// design. Only fires once per jobId, and only when the chat isn't busy.
	useEffect(() => {
		if (!finalGenerationError || !finalGenerationJobId) return;
		if (finalGenerationJobId === autoInjectedForJobId) return;
		if (!sessionId) return;
		if (session?.status === "IN_PROGRESS") return;
		if (startTurnMutation.isPending) return;

		const text =
			"⚠️ The workflow generation step failed schema validation with this error:\n\n" +
			finalGenerationError +
			"\n\nCan you help me understand what to change about the design before we try again?";

		setAutoInjectedForJobId(finalGenerationJobId);
		startTurnMutation.mutate(
			{
				id: treatmentPlanId,
				payload: { sessionId, message: text },
			},
			{
				onSuccess: () => {
					sessionQuery.refetch();
				},
			},
		);
	}, [
		finalGenerationError,
		finalGenerationJobId,
		autoInjectedForJobId,
		sessionId,
		session?.status,
		startTurnMutation,
		sessionQuery,
		treatmentPlanId,
	]);

	const isTurnInFlight =
		startTurnMutation.isPending ||
		(session?.status === "IN_PROGRESS" && !sessionQuery.isError);

	const handleSend = () => {
		const text = composer.trim();
		if (!text) return;

		startTurnMutation.mutate(
			{
				id: treatmentPlanId,
				payload: { sessionId: sessionId ?? undefined, message: text },
			},
			{
				onSuccess: (data) => {
					if (!sessionId) {
						setSessionId(data.sessionId);
						try {
							localStorage.setItem(
								sessionStorageKey(treatmentPlanId),
								data.sessionId,
							);
						} catch {
							// ignore — storage may be unavailable
						}
					} else {
						// Session status was AWAITING_INPUT, so polling was paused.
						// Force a refetch — the server has now flipped to IN_PROGRESS
						// and the refetchInterval will resume on the next tick.
						sessionQuery.refetch();
					}
					setComposer("");
					setResumeHintDismissed(true);
				},
			},
		);
	};

	// Voice stop-phrase auto-send: when the speech hook detects "send message",
	// it sets `pendingAutoSend`. We wait one tick for the appended-final
	// composer state to flush, then trigger handleSend with the current text.
	// The flag flips back to false synchronously, so this effect short-circuits
	// on subsequent renders even though it depends on changing values.
	useEffect(() => {
		if (!pendingAutoSend) return;
		setPendingAutoSend(false);
		if (composer.trim() && !isTurnInFlight) {
			handleSend();
		}
	}, [pendingAutoSend, composer, isTurnInFlight, handleSend]);

	const handleGenerate = () => {
		if (!session) return;
		onGenerate(renderRenderedDescription(session));
	};

	const confirmStartOver = () => {
		try {
			localStorage.removeItem(sessionStorageKey(treatmentPlanId));
		} catch {
			// ignore
		}
		setSessionId(null);
		setComposer("");
		setResumeHintDismissed(true);
		startTurnMutation.reset();
		setShowStartOverConfirm(false);
	};

	const canGenerate = !!session && session.messages.length > 0;

	const showEmptyState = !session && !startTurnMutation.isPending;

	const startTurnError = useMemo(() => {
		if (!startTurnMutation.isError) return null;
		const err = startTurnMutation.error as { errorMessage?: string } | Error;
		if (
			err &&
			typeof err === "object" &&
			"errorMessage" in err &&
			err.errorMessage
		) {
			return err.errorMessage;
		}
		return (err as Error)?.message ?? "Failed to send message.";
	}, [startTurnMutation.isError, startTurnMutation.error]);

	return (
		<Box>
			<Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
				<Box sx={{ flex: "1 1 60%", minWidth: 320 }}>
					<Paper
						variant="outlined"
						sx={{
							p: 2,
							display: "flex",
							flexDirection: "column",
							height: 520,
						}}
					>
						{didResumeFromStorage &&
							!resumeHintDismissed &&
							session &&
							session.messages.length > 0 && (
								<Alert
									severity="info"
									icon={<RestoreIcon fontSize="small" />}
									sx={{ mb: 1 }}
									onClose={() => setResumeHintDismissed(true)}
								>
									Resumed your earlier design session. Continue from where you
									left off.
								</Alert>
							)}

						<Box
							ref={transcriptRef}
							sx={{
								flex: 1,
								overflowY: "auto",
								pr: 1,
								mb: 1,
							}}
						>
							{showEmptyState && (
								<Typography
									variant="body2"
									color="text.secondary"
									sx={{ textAlign: "center", mt: 6 }}
								>
									Describe your program one piece at a time. The assistant will
									ask questions, confirm what it captured, and let you know when
									it's ready to generate the full workflow.
								</Typography>
							)}

							{session?.messages.map((m, idx) => (
								<ChatMessageBubble key={idx} msg={m} />
							))}

							{isTurnInFlight && (
								<Box
									sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}
								>
									<CircularProgress size={14} />
									<Typography variant="caption" color="text.secondary">
										Assistant is thinking…
									</Typography>
								</Box>
							)}

							{session?.status === "FAILED" && session.lastTurnError && (
								<Alert severity="error" sx={{ mt: 1 }}>
									{session.lastTurnError}
								</Alert>
							)}
						</Box>

						{startTurnError && (
							<Alert severity="error" sx={{ mb: 1 }}>
								{startTurnError}
							</Alert>
						)}

						{speech.error && (
							<Alert
								severity="warning"
								sx={{ mb: 1 }}
								onClose={() => {
									/* surfaced via state; harmless to keep until next start */
								}}
							>
								Speech recognition error: {speech.error}
							</Alert>
						)}

						<Stack direction="row" spacing={1} alignItems="flex-end">
							<TextField
								fullWidth
								multiline
								maxRows={4}
								size="small"
								value={
									speech.listening && speech.interim
										? (composer ? composer + " " : "") + speech.interim
										: composer
								}
								onChange={(e) => setComposer(e.target.value)}
								onKeyDown={(e) => {
									if (e.key === "Enter" && !e.shiftKey) {
										e.preventDefault();
										handleSend();
									}
								}}
								placeholder={
									session
										? "Reply or describe the next piece…"
										: "Describe your program — e.g. 'I want a 90-day buprenorphine program, let's start with day 1.'"
								}
								disabled={isTurnInFlight}
							/>
							{speech.supported && (
								<Tooltip
									title={
										speech.listening
											? "Listening… say “send message” to send, or pause for 15s to stop"
											: "Dictate your message — say “send message” to auto-send"
									}
								>
									<span>
										<IconButton
											onClick={() =>
												speech.listening ? speech.stop() : speech.start()
											}
											disabled={isTurnInFlight}
											color={speech.listening ? "error" : "default"}
											sx={
												speech.listening
													? {
															animation: "pulse 1.4s ease-in-out infinite",
															"@keyframes pulse": {
																"0%, 100%": { opacity: 1 },
																"50%": { opacity: 0.5 },
															},
														}
													: undefined
											}
										>
											{speech.listening ? <MicOffIcon /> : <MicIcon />}
										</IconButton>
									</span>
								</Tooltip>
							)}
							<IconButton
								color="primary"
								onClick={handleSend}
								disabled={isTurnInFlight || !composer.trim()}
							>
								<SendIcon />
							</IconButton>
						</Stack>
					</Paper>
				</Box>

				<Box sx={{ flex: "1 1 35%", minWidth: 280 }}>
					<DraftSidebar
						draft={session?.draft ?? null}
						status={session?.status ?? null}
						onGenerate={handleGenerate}
						canGenerate={canGenerate}
						isFinalGenerating={isFinalGenerating}
					/>
				</Box>
			</Box>

			<Box sx={{ display: "flex", gap: 2, mt: 2 }}>
				<Button
					variant="outlined"
					onClick={onCancel}
					disabled={isFinalGenerating}
				>
					Cancel
				</Button>
				{sessionId && (
					<Button
						variant="outlined"
						color="warning"
						startIcon={<RestartAltIcon />}
						onClick={() => setShowStartOverConfirm(true)}
						disabled={isTurnInFlight || isFinalGenerating}
					>
						Start over
					</Button>
				)}
			</Box>

			{showStartOverConfirm && (
				<GenericAlertModal
					title="Start a new chat?"
					description="Your current conversation will be cleared from this view. The previous session is kept on the server, but you'll start fresh here."
					submitText="Start over"
					cancelText="Cancel"
					onClick={confirmStartOver}
					onCancel={() => setShowStartOverConfirm(false)}
					icon={<RestartAltIcon fontSize="medium" />}
				/>
			)}
		</Box>
	);
};

export default SmartProgramChat;
